'use client';

import { useState, useEffect } from 'react';
import { getLatestAbandonedBooking, markBookingRecovered, clearAbandonedBooking, AbandonedBooking } from '@/lib/abandonedBooking';
import { openWhatsAppNotification, saveNotificationToStorage } from '@/lib/whatsappNotification';
import Icon from '@/components/ui/AppIcon';

export default function AbandonedBookingBanner() {
  const [booking, setBooking] = useState<AbandonedBooking | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only show on non-booking pages
    const isBookingPage = window.location?.pathname?.includes('/booking');
    if (isBookingPage) return;

    const abandoned = getLatestAbandonedBooking();
    if (abandoned) {
      setBooking(abandoned);
      // Small delay so it doesn't flash immediately on load
      setTimeout(() => setVisible(true), 2000);
    }
  }, []);

  if (!booking || !visible) return null;

  const destination = booking?.bookingDetails?.to || booking?.bookingDetails?.destination || 'your trip';
  const name = booking?.formData?.firstName || 'there';

  const handleResume = () => {
    markBookingRecovered(booking?.id);
    // Build URL with booking details
    const params = new URLSearchParams();
    if (booking?.bookingDetails) {
      Object.entries(booking?.bookingDetails)?.forEach(([k, v]) => {
        if (v !== undefined && v !== null) params?.set(k, String(v));
      });
    }
    window.location.href = `/booking?${params?.toString()}`;
  };

  const handleWhatsApp = () => {
    saveNotificationToStorage({
      type: 'booking_recovery',
      name,
      destination,
    });
    openWhatsAppNotification({
      type: 'booking_recovery',
      name,
      destination,
    });
    handleDismiss();
  };

  const handleDismiss = () => {
    setVisible(false);
    clearAbandonedBooking(booking?.id);
  };

  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-full max-w-lg px-4 animate-fade-in">
      <div className="bg-white border-2 border-amber-400 rounded-2xl shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="ClockIcon" size={20} className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-black text-foreground text-sm">
              Complete your booking, {name}! ✈️
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              You left a booking for <strong>{destination}</strong> incomplete. Resume where you left off.
            </p>
            <div className="flex gap-2 mt-3 flex-wrap">
              <button
                onClick={handleResume}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-primary to-secondary text-white text-xs font-black rounded-lg hover:shadow-md transition-all"
              >
                <Icon name="ArrowRightIcon" size={14} />
                Resume Booking
              </button>
              <button
                onClick={handleWhatsApp}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white text-xs font-black rounded-lg hover:bg-green-600 transition-all"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp Us
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
